import * as React from 'react';
import { ownerDocument } from '../../utils';
import { ResponsiveStyleValue, TabsPositioning, TabsSize, TabsValue, TabsColor, TabsVariant } from '../../theming';
import { getSafeId, TabsContext, TabsContextValues } from './Tabs.context';
import { useId } from '../../hooks';
import { moveFocus, nextItem, previousItem } from './Tabs.utils';
import { useCulture } from '../../culture';

export interface TabsProps {
    /**
     * The child `Tab` components.
     */
    children?: React.ReactNode;

    /**
     * The default value. Use when the component is not controlled.
     */
    defaultValue?: TabsValue;

    /**
     * The value of the currently selected `Tab`. Required if the component is controlled.
     */
    value?: TabsValue;

    /**
     * Callback fired when the value changes.
     * @param {React.SyntheticEvent} event The event source of the callback.
     * @param {TabsValue} value The value of the selected `tab`.
     */
    onChange?: (event: React.SyntheticEvent, value: TabsValue) => void;

    /**
     * The base id. This used to generate internal ids for accessibility markup
     * By default this is generated automatically.
     */
    baseId?: string;

    /**
     * A label for the `tablist` element. This benefits accessibility.
     */
    'aria-label'?: string;

    /**
     * The positioning behaviour of the tabs.
     * If `fullWidth`, the tabs will grow to use all available space.
     * If `centered`, the tabs will be positioned centrally.
     * If `default`, the tabs will be positioned to the left.
     * @default default
     */
    positioning?: TabsPositioning;

    /**
     * If `true`, the tabs will be underlined.
     * @default false
     */
    underline?: boolean;

    /**
     * If `true`, always keep the children in the DOM.
     * @default false
     */
    keepMounted?: boolean;

    /**
     * The style variant.
     * @default default
     */
    variant?: TabsVariant;

    /**
     * The colour of the tabs. If not specified, the default colour associated with the selected variant will be used.
     * @default default
     */
    color?: TabsColor;

    /**
     * The size of the tabs.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default m
     */
    size?: ResponsiveStyleValue<TabsSize>;
}

/**
 * Tabs are used to switch between different views of related content at the same level of hierarchy.
 */
export function Tabs(props: TabsProps): React.JSX.Element {
    const {
        'aria-label': ariaLabel,
        children,
        baseId,
        onChange,
        value: valueProp,
        defaultValue,
        positioning = 'default',
        variant = 'default',
        color = 'default',
        underline = false,
        keepMounted = false,
        size = 'm',
    } = props;

    const uid = useId(baseId);
    const { dir } = useCulture();
    const isRTL = dir === 'rtl';

    const isControlled = valueProp !== undefined;
    const [valueState, setValueState] = React.useState(defaultValue);

    const tabListRef = React.useRef<HTMLUListElement | null>(null);

    const handleChange = React.useCallback(
        (event: React.SyntheticEvent, value: TabsValue) => {
            if (!isControlled) {
                setValueState(value);
            }
            if (onChange) {
                onChange(event, value);
            }
        },
        [isControlled, onChange]
    );

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent) => {
            const list = tabListRef.current;
            if (!list) return;

            const currentFocus = ownerDocument(list).activeElement as HTMLElement;
            if (currentFocus.getAttribute('role') !== 'tab') return;

            switch (event.key) {
                case 'ArrowLeft':
                    event.preventDefault();
                    if (isRTL) {
                        moveFocus(list, currentFocus, nextItem); // left arrow points "right" visually
                    } else {
                        moveFocus(list, currentFocus, previousItem);
                    }
                    break;

                case 'ArrowRight':
                    event.preventDefault();
                    if (isRTL) {
                        moveFocus(list, currentFocus, previousItem); // right arrow points "left" visually
                    } else {
                        moveFocus(list, currentFocus, nextItem);
                    }
                    break;

                case 'Home':
                    event.preventDefault();
                    moveFocus(list, null, nextItem);
                    break;

                case 'End':
                    event.preventDefault();
                    moveFocus(list, null, previousItem);
                    break;

                default:
            }
        },
        [isRTL]
    );

    const contextValues: TabsContextValues = React.useMemo(
        () => ({
            baseId: uid,
            value: isControlled ? valueProp : valueState,
            onChange: handleChange,
            onKeyDown: handleKeyDown,
            positioning,
            underline,
            ariaLabel,
            keepMounted,
            getTabId: getSafeId(`${uid}-tab`),
            getPanelId: getSafeId(`${uid}-tabpanel`),
            variant,
            color,
            size,
            tabListRef,
        }),
        [
            uid,
            isControlled,
            valueProp,
            valueState,
            handleChange,
            handleKeyDown,
            positioning,
            underline,
            ariaLabel,
            keepMounted,
            variant,
            color,
            size,
        ]
    );

    return <TabsContext.Provider value={contextValues}>{children}</TabsContext.Provider>;
}
